import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import $ from 'jquery';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type MetricMoverService from 'webapp/services/metric-mover';

interface MetricMoverModalSignature {
	Args: object;
	Element: HTMLDivElement;
}

const CTA_SELECTOR = '.open-migration-tool-cta';
const CTA_EVENT_NAMESPACE = 'click.metricMoverCta';

export default class MetricMoverModalComponent extends Component<MetricMoverModalSignature> {
	@service('metric-mover') declare metricMoverService: MetricMoverService;

	constructor(owner: Owner, args: MetricMoverModalSignature['Args']) {
		super(owner, args);
		$(document).on(CTA_EVENT_NAMESPACE, CTA_SELECTOR, this.handleCtaClick);
	}

	override willDestroy(): void {
		super.willDestroy();
		$(document).off(CTA_EVENT_NAMESPACE, CTA_SELECTOR, this.handleCtaClick);
	}

	@action
	handleCtaClick(event: JQuery.ClickEvent): void {
		event.preventDefault();
		const spaceAccessId = $(event.currentTarget).data('space-access-id') as number | undefined;
		this.metricMoverService.open({
			spaceAccessId: spaceAccessId ? `${spaceAccessId}` : undefined,
		});
	}

	@action
	onIframeLoad(event: Event): void {
		const iframe = event.target as HTMLIFrameElement;
		if (iframe.contentWindow) {
			this.metricMoverService.registerIframe(iframe.contentWindow);
		}
	}

	@action
	closeModal(): void {
		this.metricMoverService.close();
	}

	@action
	onKeydown(event: KeyboardEvent): void {
		if (event.key === 'Escape') {
			this.closeModal();
		}
	}

	<template>
		{{#if this.metricMoverService.isOpen}}
			<div
				class="metric-mover-modal-overlay"
				{{on "keydown" this.onKeydown}}
				role="dialog"
				aria-modal="true"
				aria-label="Metric migration"
				tabindex="-1"
				...attributes
			>
				<div class="metric-mover-modal-content">
					<a
						role="button"
						class="metric-mover-modal-close"
						{{on "click" this.closeModal}}
					>
						<CommonUiIconComponent @name="Close" @size="lg" />
					</a>
					{{#if this.metricMoverService.iframeUrl}}
						<iframe
							src={{this.metricMoverService.iframeUrl}}
							{{on "load" this.onIframeLoad}}
							class="metric-mover-modal-iframe"
							title="Metric migration"
							allow="clipboard-write"
						></iframe>
					{{/if}}
				</div>
			</div>
		{{/if}}
	</template>
}
